import { AxiosError } from "axios";

type QueryError = {
  status?: number | string;
  originalStatus?: number;
  response?: { status?: number };
  name?: string;
  code?: string;
  message?: string;
  data?: unknown;
};

export type NormalizedRequestFailure = {
  kind: "permission" | "timeout" | "cancelled" | "network" | "http" | "unknown";
  status?: number;
  errorName?: string;
  errorCode?: string;
};

/**
 * Whether a rejection is a request the kiosk cancelled itself. Aborting an RTK
 * Query request rejects its thunk with an `AbortError`, which carries no HTTP
 * status and would otherwise classify as an infrastructure failure.
 */
export function isAbortError(error: unknown): boolean {
  return (
    Boolean(error) &&
    typeof error === "object" &&
    (error as { name?: unknown }).name === "AbortError"
  );
}

export function getHttpStatus(error: unknown): number | undefined {
  if (error instanceof AxiosError) return error.response?.status;
  if (!error || typeof error !== "object") return undefined;
  const queryError = error as QueryError;
  if (typeof queryError.status === "number") return queryError.status;
  return queryError.originalStatus ?? queryError.response?.status;
}

export function normalizeRequestFailure(
  error: unknown,
): NormalizedRequestFailure {
  const status = getHttpStatus(error);
  if (status === 403) return { kind: "permission", status };
  if (isAbortError(error)) return requestFailure("cancelled", error, status);

  const value =
    error && typeof error === "object" ? (error as QueryError) : undefined;
  const text = [
    value?.message,
    typeof value?.data === "string" ? value.data : undefined,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (value?.code === "ECONNABORTED" || text.includes("timeout")) {
    return requestFailure("timeout", error, status);
  }
  if (typeof status === "number") return requestFailure("http", error, status);
  if (text.includes("network")) return requestFailure("network", error, status);
  return requestFailure("unknown", error, status);
}

function requestFailure(
  kind: NormalizedRequestFailure["kind"],
  error: unknown,
  status?: number,
): NormalizedRequestFailure {
  const value =
    error && typeof error === "object" ? (error as QueryError) : undefined;
  return {
    kind,
    status,
    errorName: value?.name,
    errorCode: value?.code,
  };
}
